import type { ImportResult } from "../../types";
import {
	type PartnerData,
	parsePartnerTrackerFile,
} from "../../utils/parsePartnerTracker";
import axiosInstance from "./axiosClient";

/**
 * Parse the Excel file client-side, then send the parsed JSON to the BFF
 * for wallet matching and import. This avoids uploading a large binary
 * through CloudFront/ALB (which can timeout on big files).
 *
 * @param file    The .xlsx file to parse
 * @param dryRun  When true, returns what would change without writing
 */
export async function importPartnerTracker(
	file: File,
	dryRun: boolean,
): Promise<ImportResult> {
	// 1. Parse Excel client-side (no network needed)
	const buffer = await file.arrayBuffer();
	const partners = parsePartnerTrackerFile(buffer);

	if (partners.length === 0) {
		throw new Error(
			"No partner data found in the uploaded file. Check that the Excel file contains a 'Partner Dashboard' sheet.",
		);
	}

	// 2. Send only the parsed JSON to the BFF for matching
	const result = await axiosInstance.post<ImportResult>(
		"/api/import/partner-tracker",
		{ partners, dryRun },
		{ timeout: 30000 }, // 30s — BFF only does DB query + matching now
	);

	return result as unknown as ImportResult;
}

export type { PartnerData };
